// src/pages/MembersPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Users,
  UserPlus,
  UserCheck,
  Clock,
  Check,
  X,
  MessageCircle,
  Search,
  RotateCcw,
  UserCircle,
  UserMinus,
} from 'lucide-react';
import userService from '../services/userService';
import friendService from '../services/friendService';
import { Btn, Badge, ConfirmModal } from '../components/ui';
import ProfileModal from '../modals/ProfileModal';
import { getDisplayAge } from '../utils';
import { FAMILLES } from '../constants';

const PAGE_SIZE = 30;

const MembersPage = ({ user, toast, onUpdate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterClan, setFilterClan] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const debounceRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [removingFriendUid, setRemovingFriendUid] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  // Statuts amitié (calculés en batch)
  const [friendSet, setFriendSet] = useState(new Set());
  const [pendingSentSet, setPendingSentSet] = useState(new Set());
  const [pendingReceivedMap, setPendingReceivedMap] = useState(new Map());
  const [actionLoading, setActionLoading] = useState(null);

  // Debounce recherche
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  // Chargement initial
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;

    const load = async () => {
      const [membersList, sentResult, receivedResult] = await Promise.all([
        userService.getMembers(user),
        friendService.getPendingRequestsSent(user.uid),
        friendService.getPendingRequestsReceived(user.uid),
      ]);

      if (cancelled) return;

      setMembers(membersList);
      setFriendSet(new Set(user.friends || []));

      if (sentResult.success) {
        setPendingSentSet(new Set(sentResult.data.map((r) => r.toUid)));
      }
      if (receivedResult.success) {
        const map = new Map();
        receivedResult.data.forEach((r) => map.set(r.fromUid, r.id));
        setPendingReceivedMap(map);
      }

      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Filtrage client-side
  const filtered = members.filter((m) => {
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      if (!m.name?.toLowerCase().includes(q)) return false;
    }
    if (filterClan && m.clan !== filterClan) return false;
    if (filterCity && !m.city?.toLowerCase().includes(filterCity.toLowerCase())) return false;
    return true;
  });

  const friendCount = members.filter((m) => friendSet.has(m.uid)).length;
  const displayList =
    activeTab === 'friends' ? filtered.filter((m) => friendSet.has(m.uid)) : filtered;
  const visible = displayList.slice(0, visibleCount);
  const hasMore = visibleCount < displayList.length;

  const getStatus = (uid) => {
    if (friendSet.has(uid)) return 'friends';
    if (pendingSentSet.has(uid)) return 'pending_sent';
    if (pendingReceivedMap.has(uid)) return 'pending_received';
    return 'none';
  };

  const handleSendRequest = useCallback(
    async (targetUid) => {
      setActionLoading(targetUid);
      const result = await friendService.sendFriendRequest(targetUid);
      if (result.success) {
        setPendingSentSet((prev) => new Set([...prev, targetUid]));
        toast(t('friends.request.sent'), 'success');
      } else {
        toast(result.error || t('friends.errors.generic'), 'error');
      }
      setActionLoading(null);
    },
    [toast, t]
  );

  const handleAccept = useCallback(
    async (targetUid) => {
      const requestId = pendingReceivedMap.get(targetUid);
      if (!requestId) return;
      setActionLoading(targetUid);
      const result = await friendService.acceptFriendRequest(requestId);
      if (result.success) {
        setFriendSet((prev) => new Set([...prev, targetUid]));
        setPendingReceivedMap((prev) => {
          const next = new Map(prev);
          next.delete(targetUid);
          return next;
        });
        toast(t('friends.request.accepted'), 'success');
      } else {
        toast(result.error || t('friends.errors.generic'), 'error');
      }
      setActionLoading(null);
    },
    [pendingReceivedMap, toast, t]
  );

  const handleDecline = useCallback(
    async (targetUid) => {
      const requestId = pendingReceivedMap.get(targetUid);
      if (!requestId) return;
      setActionLoading(targetUid);
      const result = await friendService.declineFriendRequest(requestId);
      if (result.success) {
        setPendingReceivedMap((prev) => {
          const next = new Map(prev);
          next.delete(targetUid);
          return next;
        });
        toast(t('friends.request.declined'), 'success');
      } else {
        toast(result.error || t('friends.errors.generic'), 'error');
      }
      setActionLoading(null);
    },
    [pendingReceivedMap, toast, t]
  );

  const handleMessage = useCallback(
    (targetUid) => {
      const convId = `friend_${[user.uid, targetUid].sort().join('_')}`;
      navigate(`/messages/${convId}`);
    },
    [user?.uid, navigate]
  );

  const resetFilters = () => {
    setSearchQuery('');
    setFilterClan('');
    setFilterCity('');
    setVisibleCount(PAGE_SIZE);
  };

  const handleRemoveFriend = async () => {
    if (!removingFriendUid) return;
    const result = await friendService.removeFriend(removingFriendUid);
    if (result.success) {
      const name = members.find((m) => m.uid === removingFriendUid)?.name || '';
      toast(t('profile.myFriends.removeSuccess', { name }), 'success');
      setFriendSet((prev) => {
        const next = new Set(prev);
        next.delete(removingFriendUid);
        return next;
      });
      const fresh = await userService.getUser();
      if (fresh && onUpdate) onUpdate(fresh);
    } else {
      toast(result.error || t('friends.errors.generic'), 'error');
    }
    setRemovingFriendUid(null);
  };

  const hasFilters = searchQuery || filterClan || filterCity;

  return (
    <div className="max-w-5xl mx-auto px-4 pt-24 pb-32 lg:pb-8">
      <Helmet>
        <title>{t('members.meta.title')}</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://hmonghearts.com/members" />
      </Helmet>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white">
            <Users size={20} />
          </div>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('members.title')}
          </h1>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-2xl mb-4">
        <button
          data-testid="members-tab-all"
          onClick={() => {
            setActiveTab('all');
            setVisibleCount(PAGE_SIZE);
          }}
          className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2 sm:py-2.5 rounded-xl text-sm font-semibold transition ${
            activeTab === 'all'
              ? 'bg-white dark:bg-gray-700 text-violet-600 dark:text-violet-400 shadow-sm'
              : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <Users size={16} />
          {t('members.tabs.all')}
        </button>
        <button
          data-testid="members-tab-friends"
          onClick={() => {
            setActiveTab('friends');
            setVisibleCount(PAGE_SIZE);
          }}
          className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2 sm:py-2.5 rounded-xl text-sm font-semibold transition ${
            activeTab === 'friends'
              ? 'bg-white dark:bg-gray-700 text-violet-600 dark:text-violet-400 shadow-sm'
              : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <UserCheck size={16} />
          {t('members.tabs.friends')} ({friendCount})
        </button>
      </div>

      {/* Filtres */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            data-testid="members-search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('members.search')}
            className="w-full pl-9 pr-3 py-2 sm:py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500/50"
          />
        </div>
        <div className="flex gap-2 sm:contents">
          <input
            type="text"
            data-testid="members-filter-city"
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            placeholder={t('members.filterCity')}
            className="flex-1 sm:flex-none px-3 py-2 sm:py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500/50 sm:w-40"
          />
          <select
            data-testid="members-filter-clan"
            value={filterClan}
            onChange={(e) => setFilterClan(e.target.value)}
            className="flex-1 sm:flex-none px-3 py-2 sm:py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-violet-500/50 sm:w-40"
          >
            <option value="">{t('members.filterClan')}</option>
            {FAMILLES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        {hasFilters && (
          <button
            data-testid="members-reset"
            onClick={resetFilters}
            className="flex items-center gap-1.5 px-3 py-2 sm:py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition"
          >
            <RotateCcw size={14} />
            {t('members.reset')}
          </button>
        )}
      </div>

      {/* Contenu */}
      {loading ? (
        <div
          className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
          data-testid="members-skeleton"
        >
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-3 sm:p-4 animate-pulse"
            >
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
                  <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
                </div>
              </div>
              <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded-xl" />
            </div>
          ))}
        </div>
      ) : displayList.length === 0 ? (
        <div className="text-center py-12 sm:py-16" data-testid="members-empty">
          <Users size={32} className="sm:hidden mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <Users
            size={40}
            className="hidden sm:block mx-auto text-gray-300 dark:text-gray-600 mb-4"
          />
          <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
            {activeTab === 'friends'
              ? t('members.emptyFriends')
              : hasFilters
                ? t('members.emptyFiltered')
                : t('members.empty')}
          </h3>
          {activeTab === 'friends' && (
            <div className="flex justify-center mt-4">
              <Btn v="outline" onClick={() => setActiveTab('all')}>
                {t('members.tabs.browseCta')}
              </Btn>
            </div>
          )}
        </div>
      ) : (
        <>
          <div
            className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
            data-testid="members-grid"
          >
            {visible.map((member) => {
              const status = getStatus(member.uid);
              const age = getDisplayAge(member);
              const isLoading = actionLoading === member.uid;

              return (
                <div
                  key={member.uid}
                  data-testid="member-card"
                  className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-3 sm:p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedMember(member)}
                >
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    {member.profilePicture ? (
                      <img
                        src={member.profilePicture}
                        alt={member.name}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <UserCircle
                        size={40}
                        className="sm:hidden text-gray-300 dark:text-gray-600 flex-shrink-0"
                      />
                    )}
                    {!member.profilePicture && (
                      <UserCircle
                        size={48}
                        className="hidden sm:block text-gray-300 dark:text-gray-600 flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm sm:text-base font-medium sm:font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {member.name}
                        {age !== null && (
                          <span className="text-gray-400 dark:text-gray-500 font-normal text-xs sm:text-sm ml-1">
                            {age}
                          </span>
                        )}
                      </p>
                      <div className="flex items-center gap-1.5 sm:gap-2 text-xs text-gray-500 dark:text-gray-400">
                        {member.city && <span className="truncate">{member.city}</span>}
                        {member.clan && (
                          <Badge className="text-xs" data-testid="member-clan">
                            {member.clan}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1.5 sm:gap-2" onClick={(e) => e.stopPropagation()}>
                    {status === 'none' && (
                      <Btn
                        v="outline"
                        icon={UserPlus}
                        onClick={() => handleSendRequest(member.uid)}
                        loading={isLoading}
                        className="flex-1 text-xs sm:text-sm px-3 py-1.5 sm:px-4 sm:py-2"
                        data-testid="member-btn-add"
                      >
                        {t('members.addFriend')}
                      </Btn>
                    )}
                    {status === 'pending_sent' && (
                      <span
                        data-testid="member-badge-sent"
                        className="flex-1 flex items-center justify-center gap-1 sm:gap-1.5 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-400 dark:text-gray-500"
                      >
                        <Clock size={12} className="sm:hidden" />
                        <Clock size={14} className="hidden sm:block" />
                        {t('members.requestSent')}
                      </span>
                    )}
                    {status === 'pending_received' && (
                      <>
                        <Btn
                          v="outline"
                          icon={Check}
                          onClick={() => handleAccept(member.uid)}
                          loading={isLoading}
                          className="flex-1 text-xs sm:text-sm px-3 py-1.5 sm:px-4 sm:py-2 !border-emerald-300 !text-emerald-600 dark:!text-emerald-400"
                          data-testid="member-btn-accept"
                        >
                          {t('members.accept')}
                        </Btn>
                        <button
                          onClick={() => handleDecline(member.uid)}
                          data-testid="member-btn-decline"
                          className="p-1.5 sm:p-2 text-gray-400 hover:text-red-500 transition"
                          aria-label={t('members.decline')}
                        >
                          <X size={16} className="sm:hidden" />
                          <X size={18} className="hidden sm:block" />
                        </button>
                      </>
                    )}
                    {status === 'friends' && (
                      <div className="flex-1 flex items-center gap-1.5 sm:gap-2">
                        <span
                          data-testid="member-badge-friend"
                          className="flex items-center gap-1 text-xs sm:text-sm font-medium text-emerald-600 dark:text-emerald-400"
                        >
                          <UserCheck size={12} className="sm:hidden" />
                          <UserCheck size={14} className="hidden sm:block" />
                          {t('members.isFriend')}
                        </span>
                        <button
                          onClick={() => handleMessage(member.uid)}
                          data-testid="member-btn-message"
                          className="ml-auto p-1.5 sm:p-2 text-violet-500 hover:text-violet-600 dark:text-violet-400 dark:hover:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-950/30 rounded-full transition"
                          aria-label={t('members.sendMessage')}
                        >
                          <MessageCircle size={16} className="sm:hidden" />
                          <MessageCircle size={18} className="hidden sm:block" />
                        </button>
                        {activeTab === 'friends' && (
                          <button
                            onClick={() => setRemovingFriendUid(member.uid)}
                            data-testid="member-btn-remove-friend"
                            className="p-1.5 sm:p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-full transition"
                            aria-label={t('profile.myFriends.remove')}
                          >
                            <UserMinus size={16} className="sm:hidden" />
                            <UserMinus size={18} className="hidden sm:block" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {hasMore && (
            <div className="text-center mt-6">
              <Btn
                v="outline"
                onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                data-testid="members-load-more"
              >
                {t('members.loadMore')}
              </Btn>
            </div>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={!!removingFriendUid}
        onClose={() => setRemovingFriendUid(null)}
        onConfirm={handleRemoveFriend}
        title={t('profile.myFriends.removeConfirmTitle')}
        message={t('profile.myFriends.removeConfirmMessage', {
          name: members.find((m) => m.uid === removingFriendUid)?.name || '',
        })}
        confirmLabel={t('profile.myFriends.remove')}
        confirmVariant="danger"
      />

      <ProfileModal
        isOpen={!!selectedMember}
        onClose={() => setSelectedMember(null)}
        profile={selectedMember}
        context="community"
        currentUser={user}
        toast={toast}
        friendshipStatus={selectedMember ? getStatus(selectedMember.uid) : 'none'}
        onFriendAction={(action, uid) => {
          if (action === 'send') handleSendRequest(uid);
          setSelectedMember(null);
        }}
        onBlock={(blockedId) => {
          setSelectedMember(null);
          setMembers((prev) => prev.filter((m) => m.uid !== blockedId));
        }}
      />
    </div>
  );
};

export default MembersPage;
